import { createAsyncThunk } from "@reduxjs/toolkit";
import server from "../middleware/interceptor";
import { pollTaskStatus } from "./taskPolling";

export const getUserStats = createAsyncThunk<
  any, // <- return type from the API
  { start_date?: string; end_date?: string; require_admin?: boolean } | void // <- 'arg' can be optional
>(
  "admin/admin/user-stats",
  async (params = {}, thunkAPI) => {
    try {
      const response = await server.get("/admin/admin/user-stats", {
        headers: {
          "Content-Type": "application/json",
        },
        params, // optional query params
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || "Something went wrong");
    }
  }
);

export const getUserGrowth = createAsyncThunk<
  any,
  { start_date?: string; end_date?: string } | void
>(
  "admin/admin/user-growth",
  async (params = {}, thunkAPI) => {
    try {
      const response = await server.get("/admin/admin/user-growth", {
        headers: {
          "Content-Type": "application/json",
        },
        params,
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || "Something went wrong");
    }
  }
);

export const getSubscriptionStats = createAsyncThunk<
  any,
  { subscription_days?: number } | void
>(
  "admin/admin/subscriptions",
  async (params = {}, thunkAPI) => {
    try {
      const response = await server.get("/admin/admin/subscriptions", {
        headers: {
          "Content-Type": "application/json",
        },
        params,
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || "Something went wrong");
    }
  }
);

export const importKnowledgeBase = createAsyncThunk(
  "admin/knowledge-base/import",
  async ({ file }: { file: File }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await server.post("/admin/knowledge-base/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const taskId = response.data?.task_id;
      if (!taskId) {
        throw new Error("Knowledge base import task ID not returned");
      }

      const taskResponse = await pollTaskStatus(`/admin/knowledge-base/import/task-status/${taskId}`);
      const taskResult = taskResponse?.result;

      if (!taskResult?.success) {
        return thunkAPI.rejectWithValue(taskResult || "Something went wrong");
      }

      return {
        message: taskResult.message,
        summary: taskResult.summary,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || "Something went wrong");
    }
  }
);

export const getKnowledgeBaseEntries = createAsyncThunk(
  "admin/knowledge-base/entries",
  async ({ limit = 20 }: { limit?: number } = {}, thunkAPI) => {
    try {
      const response = await server.get(`/admin/knowledge-base/uploads?limit=${limit}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const deleteKnowledgeBaseUpload = createAsyncThunk(
  "admin/knowledge-base/delete-upload",
  async ({ uploadId }: { uploadId: number }, thunkAPI) => {
    try {
      const response = await server.delete(`/admin/knowledge-base/uploads/${uploadId}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const downloadKnowledgeBaseUpload = createAsyncThunk(
  "admin/knowledge-base/download-upload",
  async ({ uploadId }: { uploadId: number }, thunkAPI) => {
    try {
      const response = await server.get(`/admin/knowledge-base/uploads/${uploadId}/download`, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      return {
        data: response.data,
        headers: response.headers,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const getKnowledgeBaseUploadEntries = createAsyncThunk(
  "admin/knowledge-base/upload-entries",
  async (
    {
      uploadId,
      users_per_page = 20,
      page_number = 1,
    }: {
      uploadId: number;
      users_per_page?: number;
      page_number?: number;
    },
    thunkAPI
  ) => {
    try {
      const response = await server.get(
        `/admin/knowledge-base/uploads/${uploadId}/entries?users_per_page=${users_per_page}&page_number=${page_number}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const getKnowledgeBaseLastIngestion = createAsyncThunk(
  "admin/knowledge-base/last-ingestion",
  async (_, thunkAPI) => {
    try {
      const response = await server.get("/admin/knowledge-base/last-ingestion", {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);


export const getUserList = createAsyncThunk(
  "admin/admin/user-list",
  async ({
    users_per_page,
    page_number,
    subscription_filter,
    search
  }: {
    users_per_page: number;
    page_number: number;
    subscription_filter?: string;
    search?: string;
  }) => {
    try {
      let url = `/admin/admin/user-list?users_per_page=${users_per_page}&page_number=${page_number}`;
      if (subscription_filter) url += `&subscription_filter=${subscription_filter}`;
      if (search) url += `&search=${search}`;

      const response = await server.get(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

  export const viewUser = createAsyncThunk(
    "admin/admin/viewUser",
    async (email:any) => {
      try {
        const response = await server.get(
          `/admin/admin/user/${email}/dashboard`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        return response.data;
      } catch (error: any) {
        return error.response?.data || "Something went wrong";
      }
    }
  );

export const getQuizPerformance = createAsyncThunk(
  "admin/quiz_performance",
    async (uid:any) => {
    try {
      const response = await server.get(
        `/admin/quiz_performance?user_id=${uid}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

export const viewPlans = createAsyncThunk(
  "admin/admin/viewPlans",
  async (requiredAdmin?: boolean) => {
    try {
      const response = await server.get(
        `/plans/admin/plan${requiredAdmin === false ? "?require_admin=false" : ""}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

export const currentPlan = createAsyncThunk(
  "admin/user/current-plan",
  async (
    {
      uid,
      requiredAdmin = true,
    }: { uid: string; requiredAdmin?: boolean },
    thunkAPI
  ) => {
    try {
      const query = requiredAdmin === false ? "?require_admin=false" : "";
      const response = await server.get(
        `/plans/user/${uid}/current-plan${query}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const viewSpecificPlan = createAsyncThunk(
  "admin/admin/viewSpecificPlan",
    async (planId:any) => {
    try {
      const response = await server.get(
        `/plans/admin/plan/${planId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

export const createPlans = createAsyncThunk(
  "admin/admin/createPlans",
    async (requestBody:any) => {
    try {
      const response = await server.post(
          `/plans/admin/plan`,requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

export const deletePlan = createAsyncThunk(
  "plans/admin/plan/delete",
    async (plan_id:any) => {
    try {
      const response = await server.delete(
        `/plans/admin/plan/${plan_id}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);

export const updateAdmin = createAsyncThunk(
  "admin/admin/user/{user_id}",
  async ({ user_id, requestBody }: { user_id: string | number; requestBody: any }) => {
    try {
      const response = await server.patch(
        `/admin/admin/user/${user_id}?require_admin=true`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Something went wrong";
    }
  }
);
 
export const downloadUserList = createAsyncThunk(
  "admin/admin/download-user-list",
  async (_, thunkAPI) => {
    try {
      const response = await server.get("/chatai/interactions/export", {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const taskId = response.data?.task_id;
      if (!taskId) {
        throw new Error("Export task ID not returned");
      }

      const taskResponse = await pollTaskStatus(`/chatai/task-status/${taskId}`);
      const taskResult = taskResponse?.result;

      if (!taskResult?.success || !taskResult?.download_url) {
        return thunkAPI.rejectWithValue(taskResult || "Something went wrong");
      }

      return {
        download_url: taskResult.download_url,
        file_name: taskResult.file_name,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);
